import {
    useQuery,
    useQueryClient,
} from "@tanstack/react-query";
import {
    Check,
    CheckCircle,
    Loader2,
    Upload,
    X,
    XCircle,
} from "lucide-preact";
import {
    useCallback,
    useEffect,
    useRef,
    useState,
} from "preact/compat";
import {
    toast,
} from "react-hot-toast";

import {
    formatBytes,
} from "@mrflark/core/utils";
import type {
    UploadResponse,
    UploadStatusItem,
} from "@mrflark/spl_sdk";
import {
    Button,
    Modal,
    ProgressBar,
} from "@mrflark/ui";
import {
    splClient,
} from "../../api/client.ts";

// mirrors the ingest table spl_api accepts on POST /api/media/upload; a file
// outside it is dropped here rather than uploaded for the API to reject
const INGEST_FORMATS = [
    { extension: ".jpg", mimeType: "image/jpeg" },
    { extension: ".jpeg", mimeType: "image/jpeg" },
    { extension: ".png", mimeType: "image/png" },
    { extension: ".gif", mimeType: "image/gif" },
    { extension: ".webp", mimeType: "image/webp" },
    { extension: ".avif", mimeType: "image/avif" },
    { extension: ".bmp", mimeType: "image/bmp" },
    { extension: ".mp4", mimeType: "video/mp4" },
    { extension: ".webm", mimeType: "video/webm" },
    { extension: ".mkv", mimeType: "video/x-matroska" },
    { extension: ".mov", mimeType: "video/quicktime" },
    { extension: ".ogg", mimeType: "video/ogg" },
];

const ACCEPTED_TYPES = [
    ...new Set(INGEST_FORMATS.map((format) => format.mimeType)),
];

// the extensions are what make the OS file dialog offer the file: a browser
// converts the MIME half to extensions through its own table, and for the
// less common types that conversion yields nothing, so a MIME-only accept
// greys out every .mkv on disk.
const ACCEPT_STRING = [
    ...ACCEPTED_TYPES,
    ...INGEST_FORMATS.map((format) => format.extension),
].join(",");

// the type the API gates on: what the browser named the file, else what its
// extension says. a file neither names is not one we can upload.
const ingestTypeFor = (file: File): string | undefined => {
    if (ACCEPTED_TYPES.includes(file.type)) return file.type;
    const dot = file.name.lastIndexOf(".");
    if (dot < 0) return undefined;
    const extension = file.name.slice(dot).toLowerCase();
    return INGEST_FORMATS.find((format) => format.extension === extension)
        ?.mimeType;
};

type UploadPhase = "staging" | "uploading" | "processing" | "complete";

type FileEntry = {
    file: File;
    previewUrl: string;
};

type FileResult = {
    filename: string;
    success: boolean;
    error?: string;
};

type UploadModalProps = {
    isOpen: boolean;
    onClose: () => void;
};

const StatusIcon = ({
    status,
}: { status: UploadStatusItem["status"]; }) => {
    switch (status) {
        case "completed":
            return <Check size={16} className="text-green-500 flex-shrink-0" />;
        case "failed":
            return <XCircle size={16} className="text-red-500 flex-shrink-0" />;
        case "processing":
            return (
                <Loader2
                    size={16}
                    className="text-accent flex-shrink-0 animate-spin"
                />
            );
        default:
            return (
                <Loader2
                    size={16}
                    className="text-text-muted flex-shrink-0 animate-spin"
                />
            );
    }
};

export const UploadModal = ({
    isOpen,
    onClose,
}: UploadModalProps) => {
    const queryClient = useQueryClient();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [phase, setPhase] = useState<UploadPhase>("staging");
    const [entries, setEntries] = useState<FileEntry[]>([]);
    const [results, setResults] = useState<FileResult[]>([]);
    const [jobIds, setJobIds] = useState<string[]>([]);
    const [isDragOver, setIsDragOver] = useState(false);

    const reset = (): void => {
        for (const entry of entries) {
            URL.revokeObjectURL(entry.previewUrl);
        }
        setEntries([]);
        setResults([]);
        setJobIds([]);
        setPhase("staging");
    };

    const handleClose = (): void => {
        if (phase === "uploading") return;
        reset();
        onClose();
    };

    const addFiles = useCallback((fileList: FileList | File[]) => {
        const files: File[] = [];
        const unsupported: string[] = [];
        for (const file of Array.from(fileList)) {
            const mimeType = ingestTypeFor(file);
            if (!mimeType) {
                unsupported.push(file.name);
                continue;
            }
            // re-wrapped when the browser named the file nothing we know: the
            // multipart part carries file.type and the API gates on that
            files.push(
                mimeType === file.type
                    ? file
                    : new File([file], file.name, { type: mimeType }),
            );
        }

        // a dropped file used to leave the picker looking like it did nothing
        if (unsupported.length > 0) {
            const shown = unsupported.slice(0, 3).join(", ");
            const rest = unsupported.length - 3;
            toast.error(
                `Unsupported — ${shown}${rest > 0 ? `, +${rest}` : ""}`,
            );
        }

        if (files.length === 0) return;
        const newEntries: FileEntry[] = files.map((file) => ({
            file,
            previewUrl: URL.createObjectURL(file),
        }));
        setEntries((prev) => [...prev, ...newEntries]);
    }, []);

    const removeEntry = (index: number): void => {
        setEntries((prev) => {
            const removed = prev[index];
            if (removed) URL.revokeObjectURL(removed.previewUrl);
            return prev.filter((_, idx) => idx !== index);
        });
    };

    const handleDrop = useCallback((event: DragEvent) => {
        event.preventDefault();
        setIsDragOver(false);
        if (phase !== "staging" || !event.dataTransfer) return;
        addFiles(event.dataTransfer.files);
    }, [phase, addFiles]);

    const handleDragOver = useCallback((event: DragEvent) => {
        event.preventDefault();
        setIsDragOver(true);
    }, []);

    const handleDragLeave = useCallback(() => {
        setIsDragOver(false);
    }, []);

    const handleUpload = async (): Promise<void> => {
        if (entries.length === 0) return;
        setPhase("uploading");

        try {
            const files = entries.map((entry) => entry.file);
            const filenames = entries.map((entry) => entry.file.name);
            const response: UploadResponse = await splClient.upload(
                files,
                filenames,
            );

            const fileResults: FileResult[] = entries.map((entry) => {
                const uploaded = response.items.find(
                    (uploadedItem) =>
                        uploadedItem.original_filename === entry.file.name,
                );
                if (uploaded) {
                    return {
                        filename: entry.file.name,
                        success: true,
                    };
                }
                const uploadError = response.errors?.find(
                    (err) => err.filename === entry.file.name,
                );
                return {
                    filename: entry.file.name,
                    success: false,
                    error: uploadError?.error ?? "Upload failed",
                };
            });

            setResults(fileResults);

            const successCount = fileResults.filter((result) =>
                result.success
            ).length;
            if (successCount > 0) {
                toast.success(
                    `Uploaded ${successCount} file${
                        successCount !== 1 ? "s" : ""
                    }`,
                );
                queryClient.invalidateQueries({
                    queryKey: ["libraryItems"],
                });
            }
            if (successCount < fileResults.length) {
                const failCount = fileResults.length - successCount;
                toast.error(
                    `${failCount} file${failCount !== 1 ? "s" : ""} failed`,
                );
            }

            // transition to processing phase if we have job IDs to track
            if (response.job_ids && response.job_ids.length > 0) {
                setJobIds(response.job_ids);
                setPhase("processing");
            } else {
                setPhase("complete");
            }
        } catch (error) {
            toast.error(
                `Upload failed: ${
                    error instanceof Error ? error.message : "Unknown error"
                }`,
            );
            setPhase("staging");
        }
    };

    const {
        data: statusData,
    } = useQuery({
        queryKey: ["uploadStatus", jobIds],
        queryFn: () => splClient.getUploadStatus(jobIds),
        enabled: phase === "processing" && jobIds.length > 0,
        refetchInterval: 3000,
    });

    // transition to complete when all jobs are terminal
    useEffect(() => {
        if (phase !== "processing" || !statusData?.statuses) return;

        const allTerminal = statusData.statuses.every(
            (statusItem) =>
                statusItem.status === "completed" ||
                statusItem.status === "failed",
        );
        if (allTerminal) {
            setPhase("complete");
            queryClient.invalidateQueries({
                queryKey: ["manageItems"],
            });
        }
    }, [statusData, phase, queryClient]);

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title="Upload Media"
            size="lg"
        >
            <div className="space-y-4">
                {phase === "staging" && (
                    <>
                        <div
                            onDrop={handleDrop}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onClick={() => fileInputRef.current?.click()}
                            className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                                isDragOver
                                    ? "border-accent bg-accent/10"
                                    : "border-border hover:border-text-muted"
                            }`}
                        >
                            <Upload
                                size={32}
                                className="mx-auto mb-2 text-text-muted"
                            />
                            <p className="text-sm text-text-secondary">
                                Drag & drop files here, or click to browse
                            </p>
                            <p className="text-xs text-text-muted mt-1">
                                Images (JPEG, PNG, GIF, WebP, AVIF, BMP) and
                                Videos (MP4, WebM, MKV, MOV, OGG)
                            </p>
                            <input
                                ref={fileInputRef}
                                type="file"
                                multiple
                                accept={ACCEPT_STRING}
                                className="hidden"
                                onChange={(event) => {
                                    const files =
                                        (event.target as HTMLInputElement)
                                            .files;
                                    if (files) addFiles(files);
                                    (event.target as HTMLInputElement).value =
                                        "";
                                }}
                            />
                        </div>

                        {entries.length > 0 && (
                            <div className="space-y-2 max-h-64 overflow-y-auto">
                                {entries.map((entry, index) => (
                                    <div
                                        key={`${entry.file.name}-${index}`}
                                        className="flex items-center gap-3 p-2 bg-bg-secondary rounded-lg"
                                    >
                                        <div className="w-10 h-10 rounded overflow-hidden flex-shrink-0 bg-bg-tertiary">
                                            {entry.file.type.startsWith(
                                                    "image/",
                                                )
                                                ? (
                                                    <img
                                                        src={entry.previewUrl}
                                                        alt=""
                                                        className="w-full h-full object-cover"
                                                    />
                                                )
                                                : (
                                                    <video
                                                        src={entry.previewUrl}
                                                        className="w-full h-full object-cover"
                                                    />
                                                )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm text-text-primary truncate">
                                                {entry.file.name}
                                            </p>
                                            <p className="text-xs text-text-muted">
                                                {formatBytes(entry.file.size)}
                                            </p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => removeEntry(index)}
                                            className="p-1 text-text-muted hover:text-text-primary transition-colors"
                                        >
                                            <X size={16} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}

                {phase === "uploading" && (
                    <div className="py-8 space-y-4">
                        <p className="text-sm text-text-secondary text-center">
                            Uploading {entries.length}{" "}
                            file{entries.length !== 1 ? "s" : ""}...
                        </p>
                        <div className="relative h-2">
                            <ProgressBar percent={100} />
                        </div>
                    </div>
                )}

                {phase === "processing" && statusData && (
                    <div className="space-y-3">
                        <p className="text-sm text-text-secondary text-center">
                            Processing uploads...
                        </p>
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                            {statusData.statuses.map((statusItem) => (
                                <div
                                    key={statusItem.job_id}
                                    className="flex items-center gap-3 p-2 bg-bg-secondary rounded-lg"
                                >
                                    <StatusIcon status={statusItem.status} />
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm text-text-primary">
                                            Job #{statusItem.job_id}
                                            {statusItem.media_id && (
                                                <span className="text-text-muted ml-1 text-xs">
                                                    ({statusItem.media_id.slice(
                                                        0,
                                                        8,
                                                    )})
                                                </span>
                                            )}
                                        </p>
                                        <p className="text-xs text-text-muted capitalize">
                                            {statusItem.status}
                                        </p>
                                        {statusItem.error && (
                                            <p className="text-xs text-red-400">
                                                {statusItem.error}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {phase === "processing" && !statusData && (
                    <div className="py-8 flex items-center justify-center gap-2">
                        <Loader2
                            size={18}
                            className="animate-spin text-text-muted"
                        />
                        <p className="text-sm text-text-secondary">
                            Checking processing status...
                        </p>
                    </div>
                )}

                {phase === "complete" && (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                        {results.map((result, index) => (
                            <div
                                key={`${result.filename}-${index}`}
                                className="flex items-center gap-3 p-2 bg-bg-secondary rounded-lg"
                            >
                                {result.success
                                    ? (
                                        <CheckCircle
                                            size={18}
                                            className="text-green-500 flex-shrink-0"
                                        />
                                    )
                                    : (
                                        <XCircle
                                            size={18}
                                            className="text-red-500 flex-shrink-0"
                                        />
                                    )}
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm text-text-primary truncate">
                                        {result.filename}
                                    </p>
                                    {result.error && (
                                        <p className="text-xs text-red-400">
                                            {result.error}
                                        </p>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                <div className="flex justify-end gap-2 pt-2 border-t border-border">
                    {phase === "staging" && (
                        <>
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={handleClose}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                size="sm"
                                onClick={handleUpload}
                                disabled={entries.length === 0}
                            >
                                <Upload size={14} className="mr-1" />
                                Upload {entries.length > 0
                                    ? `(${entries.length})`
                                    : ""}
                            </Button>
                        </>
                    )}
                    {phase === "processing" && (
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={handleClose}
                        >
                            Close
                        </Button>
                    )}
                    {phase === "complete" && (
                        <>
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={reset}
                            >
                                Upload More
                            </Button>
                            <Button
                                variant="primary"
                                size="sm"
                                onClick={handleClose}
                            >
                                Done
                            </Button>
                        </>
                    )}
                </div>
            </div>
        </Modal>
    );
};
